import React, { useState, useEffect, Suspense } from 'react';
import { BounceLoader } from 'react-spinners';

const fetchNewMaps = () => {
  return new Promise((resolve) => {
    fetch('https://surfapi.insanitygaming.net/api/map?limit=100&min_tier=1&&max_tier=6&&order=4', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        }
        return response.json();
      })
      .then((data) => {
        if (data.status === 200) {
          resolve(data.maps || []); // Ensure correct key for maps data
        } else {
          throw new Error('Failed to fetch data');
        }
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
        resolve([]); // Resolve with empty array on error
      });
  });
};

function getRelativeTimeString(oldDate) {
    const now = new Date();
    const seconds = Math.floor((now - oldDate) / 1000);
    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

    if (seconds < 60) {
        return rtf.format(-seconds, 'second');
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
        return rtf.format(-minutes, 'minute');
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        return rtf.format(-hours, 'hour');
    }

    const days = Math.floor(hours / 24);
    if (days < 30) {
        return rtf.format(-days, 'day');
    }

    const months = Math.floor(days / 30);
    if (months < 12) {
        return rtf.format(-months, 'month');
    }

    const years = Math.floor(months / 12);
    return rtf.format(-years, 'year');
}

const NewMapsList = () => {
  const [newMaps, setNewMaps] = useState([]);
  const [loading, setLoading] = useState(true); // Loading state
  const [currentPage, setCurrentPage] = useState(1);
  const mapsPerPage = 10;

  useEffect(() => {
    fetchNewMaps().then((data) => {
      setNewMaps(data);
      setLoading(false); // Stop loading when data is fetched
    });
  }, []);

  // Pagination logic
  const totalPages = Math.ceil((newMaps?.length || 0) / mapsPerPage);
  const currentPageData = newMaps.slice(
    (currentPage - 1) * mapsPerPage,
    currentPage * mapsPerPage
  );

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  if (loading) {
    return (
		<section className="p-4 bg-gray-800 shadow rounded-lg h-full flex flex-col">
			<h4 className="font-bold mb-2 text-gray-200">Newest Maps</h4>
			<div className="relative inset-0 flex justify-center items-center bg-gray-800 bg-opacity-75 z-10">
			<BounceLoader size={50} color="#4CAF50" />
			</div>
		</section>
    );
  }

  return (
    <section className="p-4 bg-gray-800 shadow rounded-lg h-full flex flex-col">
      <h4 className="font-bold mb-2 text-gray-200">Newest Maps</h4>
      <div className="overflow-y-auto flex-grow">
        <table className="min-w-full table-auto text-left text-gray-300">
          <thead>
            <tr>
              <th className="px-4 py-2 border-b h-12">Name</th>
              <th className="px-4 py-2 border-b h-12">Age</th>
            </tr>
          </thead>
          <tbody>
            {currentPageData.map((map, idx) => (
              <tr key={`${map.user_id}-${idx}`} className="h-12">
                <td
                  className="px-4 py-2 border-b h-12 whitespace-nowrap overflow-hidden text-ellipsis max-w-[150px]"
                  title={map.name}
                >
                  {map.name}
                </td>
                <td className="px-4 py-2 border-b h-12">{getRelativeTimeString(new Date(map.first_seen))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="flex justify-center mt-4">
        <button
          className={`px-4 py-2 mx-1 rounded-md ${
            currentPage === 1 ? 'bg-gray-600 text-gray-400' : 'bg-gray-700 text-white hover:bg-gray-600'
          }`}
          disabled={currentPage === 1}
          onClick={handlePrev}
        >
          Prev
        </button>
        <span className="px-4 py-2 text-gray-300">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className={`px-4 py-2 mx-1 rounded-md ${
            currentPage === totalPages ? 'bg-gray-600 text-gray-400' : 'bg-gray-700 text-white hover:bg-gray-600'
          }`}
          disabled={currentPage === totalPages}
          onClick={handleNext}
        >
          Next
        </button>
      </div>
    </section>
  );
};

const NewMaps = () => {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <NewMapsList />
    </Suspense>
  );
};

export default NewMaps;
